import React from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Undo2, Trash2, ChevronDown, ChevronRight, History, Search } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

// iter495h — Group + filter helper: sort a batch's items by client name
// (then by site name) and optionally filter by a case-insensitive
// search term matching client OR site.
function groupBatchItems(items, search) {
  const q = (search || '').trim().toLowerCase();
  const filtered = q
    ? (items || []).filter(it =>
      (it.client_name || '').toLowerCase().includes(q)
      || (it.site_name || '').toLowerCase().includes(q))
    : (items || []);
  const buckets = new Map();
  const order = [];
  for (const it of filtered) {
    const key = it.client_name || 'Unassigned';
    if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
    buckets.get(key).push(it);
  }
  // Alphabetical client order.
  order.sort((a, b) => a.localeCompare(b));
  for (const k of order) {
    buckets.get(k).sort((a, b) => (a.site_name || '').localeCompare(b.site_name || ''));
  }
  return order.map(k => ({ client_name: k, items: buckets.get(k) }));
}

// iter495e — Publish History
// Lists every Review & Publish batch this org has created, newest
// first. Each batch expands to its line items. Two actions available
// per batch:
//   * Undo  — cancels every schedule row + restores metadata snapshots.
//   * Delete — same as Undo (if not already) + soft-deletes the batch
//             from the list.
export function PublishHistoryDialog({ onClose, onChanged }) {
  const { toast } = useToast();
  const [loading, setLoading] = React.useState(true);
  const [batches, setBatches] = React.useState([]);
  const [expandedId, setExpandedId] = React.useState(null);
  const [expandedItems, setExpandedItems] = React.useState({}); // batch_id -> items[]
  const [busyId, setBusyId] = React.useState(null);
  // iter495h — Per-batch search box state (independent per open batch).
  const [searchByBatch, setSearchByBatch] = React.useState({});
  // Top-level filter across the batch list (matches user / note / date).
  const [globalSearch, setGlobalSearch] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/reports/revenue-budget/publish-batches');
      setBatches(r.data || []);
    } catch (err) {
      toast({ title: 'Could not load publish history', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  React.useEffect(() => { load(); }, [load]);

  const toggle = async (batch) => {
    if (expandedId === batch.id) {
      setExpandedId(null); return;
    }
    setExpandedId(batch.id);
    if (!expandedItems[batch.id]) {
      try {
        const r = await api.get(`/reports/revenue-budget/publish-batches/${batch.id}`);
        setExpandedItems(prev => ({ ...prev, [batch.id]: r.data?.items || [] }));
      } catch (err) {
        toast({ title: 'Could not load batch', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
      }
    }
  };

  const undo = async (batch) => {
    setBusyId(batch.id);
    try {
      const r = await api.post(`/reports/revenue-budget/publish-batches/${batch.id}/undo`);
      toast({ title: 'Batch undone', description: `${r.data?.cancelled_rows || 0} scheduled change(s) cancelled.` });
      await load();
      if (onChanged) await onChanged();
    } catch (err) {
      toast({ title: 'Undo failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const remove = async (batch) => {
    if (!confirm(`Delete this batch? This will undo every change and remove the entry from the history.`)) return;
    setBusyId(batch.id);
    try {
      const r = await api.delete(`/reports/revenue-budget/publish-batches/${batch.id}`);
      toast({ title: 'Batch deleted', description: `${r.data?.cancelled_rows || 0} scheduled change(s) cancelled and removed from history.` });
      await load();
      if (onChanged) await onChanged();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="publish-history-dialog"
        className="max-w-[95vw] w-[95vw] max-h-[85vh] flex flex-col overflow-hidden"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" /> Publish History
          </DialogTitle>
          <p className="text-xs text-slate-500 pt-0.5">
            Every Review &amp; Publish batch, newest first. Undo cancels the batch&apos;s scheduled changes; Delete does the same and removes the entry from this list.
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-2">
          {/* Global list search (matches user / note / date). */}
          <div className="relative max-w-md">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              type="search"
              placeholder="Filter batches by user, note or date…"
              className="pl-7 h-8 text-xs"
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              data-testid="publish-history-search"
            />
          </div>
          {loading && (
            <div className="flex items-center gap-2 text-slate-500 py-4 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && batches.length === 0 && (
            <div className="text-xs text-slate-500 italic py-6 text-center">No batches yet. Every Review &amp; Publish you run will show up here.</div>
          )}
          {!loading && batches
            .filter(b => {
              const q = globalSearch.trim().toLowerCase();
              if (!q) return true;
              return (b.created_by_name || '').toLowerCase().includes(q)
                  || (b.note || '').toLowerCase().includes(q)
                  || new Date(b.created_at).toLocaleString().toLowerCase().includes(q);
            })
            .map((b, filteredIdx, arr) => {
              // iter495q — Reverse-order guard. Only the newest batch
              // that's still active (not undone and not deleted) can
              // be reversed. Older active batches are disabled with a
              // tooltip that explains why.
              const activeBatches = batches
                .filter(x => !x.undone_at && !x.deleted_at)
                .sort((a, c) => new Date(c.created_at) - new Date(a.created_at));
              const newestActiveId = activeBatches[0]?.id;
              const isBlocked = !b.undone_at && !b.deleted_at && newestActiveId !== b.id;
            const undone = !!b.undone_at;
            const created = new Date(b.created_at).toLocaleString();
            const totalSources = Object.entries(b.summary?.sources || {}).map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`).join(' · ');
            return (
              <div
                key={b.id}
                className={`rounded-lg border ${undone ? 'bg-slate-50 border-slate-200 opacity-70' : 'bg-white border-slate-200'}`}
                data-testid={`publish-batch-${b.id}`}
              >
                <div className="flex items-center gap-2 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => toggle(b)}
                    className="flex items-center gap-1 text-slate-700 hover:text-slate-900"
                    data-testid={`publish-batch-toggle-${b.id}`}
                  >
                    {expandedId === b.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className="font-semibold text-slate-800">#{b.id}</span>
                      <span className="text-slate-500">·</span>
                      <span>{created}</span>
                      <span className="text-slate-500">·</span>
                      <span>{b.created_by_name || '—'}</span>
                      {undone && (
                        <span className="rounded-full bg-rose-100 text-rose-800 text-[10px] px-2 py-0.5 font-semibold ml-1">Undone</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {totalSources || '—'}
                      {b.row_count > 0 && <span> · {b.row_count} live schedule row{b.row_count === 1 ? '' : 's'}</span>}
                      {b.note && <span> · {b.note}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!undone && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => undo(b)}
                        disabled={busyId === b.id || isBlocked}
                        title={isBlocked ? `Undo batch #${newestActiveId} first — batches must be reversed newest-first.` : 'Undo this batch'}
                        data-testid={`publish-batch-undo-${b.id}`}
                        className="text-rose-700 hover:text-rose-800"
                      >
                        <Undo2 className="h-3.5 w-3.5 mr-1" /> Undo
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(b)}
                      disabled={busyId === b.id || isBlocked}
                      title={isBlocked ? `Delete batch #${newestActiveId} first — batches must be reversed newest-first.` : 'Delete this batch (undo + remove from history)'}
                      data-testid={`publish-batch-delete-${b.id}`}
                      className="text-slate-500 hover:text-rose-700"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {expandedId === b.id && (
                  <div className="px-3 pb-3 border-t bg-slate-50/60">
                    {!expandedItems[b.id] && (
                      <div className="text-xs text-slate-500 py-2 flex items-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" /> Loading rows…
                      </div>
                    )}
                    {expandedItems[b.id] && expandedItems[b.id].length === 0 && (
                      <div className="text-[11px] italic text-slate-500 py-2">No schedule rows on this batch (metadata-only push).</div>
                    )}
                    {expandedItems[b.id] && expandedItems[b.id].length > 0 && (
                      <div className="mt-2 space-y-2">
                        {/* iter495h — Search box for this batch's rows. */}
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                            <Input
                              type="search"
                              placeholder="Filter by site or client…"
                              className="pl-7 h-7 text-[11px]"
                              value={searchByBatch[b.id] || ''}
                              onChange={(e) => setSearchByBatch(s => ({ ...s, [b.id]: e.target.value }))}
                              data-testid={`publish-batch-search-${b.id}`}
                            />
                          </div>
                        </div>
                        <div className="border rounded-md overflow-hidden bg-white">
                          <table className="w-full text-xs">
                            <thead className="bg-slate-100 text-slate-600">
                              <tr>
                                <th className="text-left px-2 py-1.5">Site</th>
                                <th className="text-right px-2 py-1.5">Price</th>
                                <th className="text-left px-2 py-1.5">Frequency</th>
                                <th className="text-left px-2 py-1.5">Effective</th>
                                <th className="text-left px-2 py-1.5">Source</th>
                                <th className="text-left px-2 py-1.5">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupBatchItems(expandedItems[b.id], searchByBatch[b.id]).map(({ client_name, items }) => (
                                <React.Fragment key={`ph-${b.id}-${client_name}`}>
                                  <tr className="bg-slate-50 border-t">
                                    <td colSpan={6} className="px-2 py-1 font-semibold uppercase text-[10px] tracking-wide text-slate-600">
                                      {client_name} <span className="text-slate-400 font-normal normal-case">· {items.length} row{items.length === 1 ? '' : 's'}</span>
                                    </td>
                                  </tr>
                                  {items.map(it => (
                                    <tr key={`bit-${it.id}`} className="border-t" data-testid={`publish-batch-item-${it.id}`}>
                                      <td className="px-2 py-1.5 font-medium pl-6">{it.site_name}</td>
                                      <td className="px-2 py-1.5 text-right font-mono">{it.price != null ? `£${Number(it.price).toFixed(2)}` : '—'}</td>
                                      <td className="px-2 py-1.5">{it.frequency || '—'}</td>
                                      <td className="px-2 py-1.5">{it.effective_from}</td>
                                      <td className="px-2 py-1.5 text-[11px] text-slate-500">{(it.source || '').replace(/_/g, ' ')}</td>
                                      <td className="px-2 py-1.5">
                                        {it.cancelled_at
                                          ? <span className="text-rose-700">cancelled</span>
                                          : <span className="text-emerald-700">active</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                              {groupBatchItems(expandedItems[b.id], searchByBatch[b.id]).length === 0 && (
                                <tr><td colSpan={6} className="px-2 py-3 text-center text-[11px] italic text-slate-500">No rows match &quot;{searchByBatch[b.id]}&quot;.</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t bg-background">
          <Button variant="outline" onClick={onClose} data-testid="publish-history-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
